import { computed, ref, Ref } from '@vue/composition-api';
import compareVersions from 'compare-versions';
import { Block } from '../types';
import { useBlocks as useAvailableBlocks, useFuse } from '@/app/composable';
import { isNil } from 'ramda';

const isVersionBetween = (version: string, from: string, to: string): boolean => {
    if (version === 'latest') return isNil(to);
    return (!from || compareVersions(from, version) <= 0) && (!to || compareVersions(to, version) >= 0);
};

const matchesCriteria = (
    block: Block,
    filter: string | undefined,
    framework: string | undefined,
    platform: string | undefined,
    version: string | undefined,
): boolean => {
    // Must be analytics type
    if (block.executionImageType !== 'analytics') return false;

    // Must have platform defined
    if (platform == null) return false;

    // Must match category filter
    if (filter != null && block.category !== filter) return false;

    // Must match framework
    const frameworkMatch =
        (Array.isArray(block.frameworks) && block.frameworks.includes(framework as any)) ||
        block.frameworks === framework;
    if (!frameworkMatch) return false;

    // Must match platform
    const platformMatch =
        !('platforms' in block) ||
        (Array.isArray(block.platforms) && block.platforms.includes(platform as any)) ||
        block.platforms === platform;
    if (!platformMatch) return false;

    // Must be compatible with framework version
    if (!version || !isVersionBetween(version, block.executionImageVersionFrom, block.executionImageVersionTo)) {
        return false;
    }

    return true;
};

export function useBlocks(
    searchText?: Ref<string>,
    activeFilter?: Ref<string | undefined>,
    framework?: string | undefined,
    frameworkVersion?: Ref<string | undefined>,
    platform?: string | undefined,
) {
    const { blocks, loading, blocksMap, refetch } = useAvailableBlocks();

    const { result: filteredOptions } = useFuse<Block>(searchText || ref(''), blocks as any, {
        keys: ['name', 'description', 'type'],
        threshold: 0.2,
        ignoreLocation: true,
    });

    const visibleBlocks = computed(() => {
        const version = frameworkVersion?.value;
        const filter = activeFilter?.value;

        // Single-pass filter
        const filtered = filteredOptions.value.filter((b) => matchesCriteria(b, filter, framework, platform, version));

        // Keep only latest version per block ID
        const latestByBlockId = new Map<string, Block>();
        for (const block of filtered) {
            const existing = latestByBlockId.get(block.id);
            if (!existing || block.version > existing.version) {
                latestByBlockId.set(block.id, block);
            }
        }

        // Sort by type then name
        const sorted = Array.from(latestByBlockId.values()).sort((a, b) => {
            const typeA = a.type || 'other';
            const typeB = b.type || 'other';
            if (typeA !== typeB) return typeA.localeCompare(typeB);
            return a.name.localeCompare(b.name);
        });

        // Group by type, ensuring 'other' comes last
        const grouped: Record<string, Block[]> = {};
        for (const block of sorted) {
            const type = block.type || 'other';
            if (type !== 'other') {
                (grouped[type] ??= []).push(block);
            }
        }
        // Add 'other' last if it exists
        const otherBlocks = sorted.filter((b) => !b.type);
        if (otherBlocks.length > 0) {
            grouped.other = otherBlocks;
        }

        return grouped;
    });

    return { blocks, visibleBlocks, loading, blocksMap, refetch, filteredOptions };
}
